// Calendar-date helper built on the Bridge-emitted Accent.Core.Common.DataTypes.InsyteDate
// (loaded from public/InsyteHtml/<version>/InsyteHTML.js, the same way AccentLength* use
// window.InsyteConfig.MeasuredValue).
//
// The wire / storage shape for an InsyteDate on the client is ALWAYS the "yyyy-MM-dd" string:
// Breeze metadata exposes the server InsyteDate? properties as String, the product model JSON
// carries the same literal and the server parses it strictly. Nothing in here ever produces an
// ISO date-time or touches a time zone, so a time-of-day can never creep back in.
//
// JS Date instances are only used at the edge (Kendo DatePicker in / out) and are always read
// or built with the LOCAL calendar components (getFullYear/getMonth/getDate, new Date(y, m-1, d)).
// Never use new Date("yyyy-MM-dd") - that parses as UTC midnight and shifts a day in AU/NZ.

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const pad = (n, w) => `${n}`.padStart(w, '0');

const getBridgeType = () => window.Accent?.Core?.Common?.DataTypes?.InsyteDate;
const getBridgeClock = () => window.Accent?.Core?.Common?.DataTypes?.InsyteClock;

const isNullish = v => v === null || v === undefined || v === '';

// IANA zone the app's "today" is evaluated in (the business unit's zone), installed by installClock.
// null means "not installed yet": fall back to the browser's local calendar day.
let clockZone = null;

// "yyyy-MM-dd" of the current instant in an IANA zone. en-CA is the one locale whose default date
// format is ISO order; the explicit 2-digit options keep it stable across engines.
const isoTodayInZone = (timeZone) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(new Date());
    const get = type => parts.find(p => p.type === type)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
};

export const insyteDate = {

    /** "yyyy-MM-dd" */
    format: 'yyyy-MM-dd',

    /** The Bridge struct type (undefined until InsyteHTML.js has loaded). */
    get Type() {
        return getBridgeType();
    },

    /** True when v is an instance of the Bridge InsyteDate struct. */
    isInsyteDate(v) {
        const type = getBridgeType();
        if (isNullish(v) || !type || !window.Bridge) return false;
        return window.Bridge.is(v, type);
    },

    /** Strict "yyyy-MM-dd" text test (no whitespace, time or offset). */
    isIsoDateString(v) {
        return typeof v === 'string' && ISO_DATE_ONLY.test(v) && this.tryParse(v) !== null;
    },

    /** Bridge InsyteDate from y/m/d, or null if out of range. */
    fromParts(year, month, day) {
        const type = getBridgeType();
        if (!type) return null;
        try {
            return new type.$ctor2(year, month, day);
        } catch (e) {
            return null;
        }
    },

    /** Strict parse of "yyyy-MM-dd" into a Bridge InsyteDate; null for anything else. */
    tryParse(s) {
        const type = getBridgeType();
        if (typeof s !== 'string' || !type) return null;
        const result = { v: null };
        return type.TryParse(s, result) ? result.v : null;
    },

    /** Bridge InsyteDate for the LOCAL calendar date of a JS Date (time discarded, no zone maths). */
    fromJsDate(d) {
        if (!(d instanceof Date) || isNaN(d.getTime())) return null;
        return this.fromParts(d.getFullYear(), d.getMonth() + 1, d.getDate());
    },

    /**
     * Coerce anything we may be handed (Bridge InsyteDate, "yyyy-MM-dd", a JS Date from a picker,
     * or a legacy date-time string) into a Bridge InsyteDate, or null.
     * Legacy date-time strings are the only lenient path: they are interpreted through the
     * browser's local clock exactly as the old AccentDate controls did.
     */
    toBridge(v) {
        if (isNullish(v)) return null;
        if (this.isInsyteDate(v)) return v;
        if (v instanceof Date) return this.fromJsDate(v);
        if (typeof v === 'string') {
            const strict = this.tryParse(v);
            if (strict !== null) return strict;
            // Looks like "yyyy-MM-dd" but isn't a real date (e.g. 2026-02-30): reject rather than let
            // the JS Date parser roll it over into March.
            if (ISO_DATE_ONLY.test(v)) return null;
            const legacy = new Date(v);
            return isNaN(legacy.getTime()) ? null : this.fromJsDate(legacy);
        }
        return null;
    },

    /** The canonical "yyyy-MM-dd" string for any accepted input, or null. */
    toString(v) {
        if (typeof v === 'string' && ISO_DATE_ONLY.test(v) && this.tryParse(v) !== null) return v;
        const d = this.toBridge(v);
        if (d === null) return null;
        // Bridge InsyteDate.toString() is always "yyyy-MM-dd"; no culture/zone involved.
        return d.toString();
    },

    /** Alias of toString: the value to store on entities / product models / filters. */
    normalize(v) {
        return this.toString(v);
    },

    /** Local-midnight JS Date for the picker, or null. */
    toJsDate(v) {
        const d = this.toBridge(v);
        if (d === null) return null;
        return new Date(d.Year, d.Month - 1, d.Day);
    },

    /**
     * Today's calendar date on the browser's clock (or in the zone given to installClock, if any). The
     * browser's zone travels to the server in the insytetz header, so the server's InsyteDate.Today
     * resolves the same day for this user's requests.
     */
    today() {
        return this.todayInZone(clockZone);
    },

    /** Today's calendar date in an IANA zone; null/invalid zone falls back to the browser's local day. */
    todayInZone(timeZone) {
        if (!isNullish(timeZone)) {
            try {
                const parsed = this.tryParse(isoTodayInZone(timeZone));
                if (parsed !== null) return parsed;
            } catch (e) {
                // unknown zone id: fall through to the local day
            }
        }
        return this.fromJsDate(new Date());
    },

    /**
     * Install the zone "today" is evaluated in and hand the same rule to the Bridge bundle, so every
     * InsyteDate.Today inside the shared C# (configurator rules, OutOfStockDiscontinuedResult defaults,
     * CURRENTDATE) resolves the same day as this helper and as the server. Pass an IANA id (map the
     * business unit's Windows id with windowsToIana first); a null zone uninstalls and reverts to local.
     */
    installClock(timeZone) {
        clockZone = isNullish(timeZone) ? null : timeZone;
        const clock = getBridgeClock();
        if (clock) {
            clock.TodayProvider = clockZone === null ? null : () => this.todayInZone(clockZone);
        }
        return clockZone;
    },

    /** The IANA zone installed by installClock, or null when running on the browser's local day. */
    get clockZone() {
        return clockZone;
    },

    /** -1 / 0 / 1 like C# CompareTo; nulls sort first. */
    compare(a, b) {
        const da = this.toBridge(a);
        const db = this.toBridge(b);
        if (da === null && db === null) return 0;
        if (da === null) return -1;
        if (db === null) return 1;
        return da.compareTo(db);
    },

    equals(a, b) {
        return this.compare(a, b) === 0;
    },

    /** Localised short date text (e.g. 15/01/2026 for en-AU) without any zone conversion. */
    formatDisplay(v, locale) {
        const d = this.toBridge(v);
        if (d === null) return '';
        const jsDate = new Date(d.Year, d.Month - 1, d.Day);
        try {
            return new Intl.DateTimeFormat(locale || undefined).format(jsDate);
        } catch (e) {
            return `${pad(d.Day, 2)}/${pad(d.Month, 2)}/${pad(d.Year, 4)}`;
        }
    }
};

export default insyteDate;
